import { FileProcessingResultStatus } from '@atlas/types'
import type { ConcernState } from '@atlas/ui-components'
import type { FileDTO } from '@/services/file.service'

/**
 * How one concern finished, for a per-concern callback.
 *
 * `reason` distinguishes the two non-success cases because callers act on them
 * differently: `failed` is worth telling the user about and retrying, whereas
 * `absent` (the server never created the row — e.g. no tuples were submitted)
 * is expected and merely means the caller's follow-up should proceed anyway.
 */
export type ConcernOutcome =
	| { ok: true; file: FileDTO }
	| { ok: false; reason: 'failed' | 'absent'; file: FileDTO; errorMessage?: string }

export type SettledCallback = (file: FileDTO) => void | Promise<void>
export interface ConcernRegistration {
	concernId: string
	run: (outcome: ConcernOutcome) => void | Promise<void>
}

/**
 * Per-item callback bookkeeping for the upload store: which callbacks are
 * registered, which have already fired, and the confirmed DTO each needs.
 *
 * Split out of the store so the store file stays about queue state. The
 * fired-once guarantee lives here because it is the whole reason this needs
 * state at all — a callback that ran twice would double-write an audit entry.
 */
class UploadCallbackRegistry {
	/** Confirmed DTOs, held until a callback needs to be handed the file. */
	private readonly confirmedFiles = new Map<string, FileDTO>()
	private readonly settled = new Map<string, SettledCallback>()
	private readonly firedSettled = new Set<string>()
	private readonly concerns = new Map<string, ConcernRegistration>()
	/** Keys are `${itemId}:${concernId}`, so one concern's callback fires once. */
	private readonly firedConcerns = new Set<string>()

	register(id: string, options: { onSettled?: SettledCallback; onConcernSettled?: ConcernRegistration }): void {
		if (options.onSettled) this.settled.set(id, options.onSettled)
		if (options.onConcernSettled) this.concerns.set(id, options.onConcernSettled)
	}

	rememberConfirmed(id: string, file: FileDTO): void {
		this.confirmedFiles.set(id, file)
	}

	forget(id: string): void {
		this.confirmedFiles.delete(id)
		this.settled.delete(id)
		this.firedSettled.delete(id)
		const concernId = this.concerns.get(id)?.concernId
		if (concernId) this.firedConcerns.delete(`${id}:${concernId}`)
		this.concerns.delete(id)
	}

	clear(): void {
		this.confirmedFiles.clear()
		this.settled.clear()
		this.firedSettled.clear()
		this.concerns.clear()
		this.firedConcerns.clear()
	}

	/** Fires the settle callback the first time an item is fully settled. */
	fireSettled(id: string, isItemSettled: boolean): void {
		if (!isItemSettled || this.firedSettled.has(id)) return
		const file = this.confirmedFiles.get(id)
		if (!file) return
		this.firedSettled.add(id)
		runDetached(() => this.settled.get(id)?.(file))
	}

	/**
	 * Fires the per-concern callback once that concern is terminal.
	 *
	 * `isConfirmed` gates the `absent` outcome: before confirm the row may still
	 * be created, so a missing entry is not yet an answer.
	 */
	fireConcern(id: string, concerns: Record<string, ConcernState>, isConfirmed: boolean): void {
		const registration = this.concerns.get(id)
		if (!registration || !isConfirmed) return
		const key = `${id}:${registration.concernId}`
		if (this.firedConcerns.has(key)) return
		const file = this.confirmedFiles.get(id)
		if (!file) return

		const outcome = resolveConcernOutcome(concerns[registration.concernId], file)
		if (!outcome) return

		this.firedConcerns.add(key)
		runDetached(() => registration.run(outcome))
	}
}

/** `null` while the concern is still pending/running — nothing terminal to report yet. */
function resolveConcernOutcome(entry: ConcernState | undefined, file: FileDTO): ConcernOutcome | null {
	if (!entry) return { ok: false, reason: 'absent', file }
	if (entry.status === FileProcessingResultStatus.COMPLETED) return { ok: true, file }
	// SKIPPED reports alongside FAILED, not as "still running": the concern never
	// ran and never will on this pass, so waiting for it would leave the caller's
	// follow-up work pending forever — the exact stranding this callback exists to
	// prevent, where a file is stored but attached to nothing with no error shown.
	if (entry.status === FileProcessingResultStatus.FAILED || entry.status === FileProcessingResultStatus.SKIPPED) {
		return { ok: false, reason: 'failed', file, errorMessage: entry.errorMessage }
	}
	return null
}

/**
 * Runs a consumer callback detached from the caller's promise chain, so neither
 * a throw nor a rejection can propagate back into the upload's own outcome.
 * Callers own their error reporting; the store only guarantees each fires once.
 */
export function runDetached(run: () => void | Promise<void>): void {
	void Promise.resolve()
		.then(run)
		.catch(() => undefined)
}

export const uploadCallbacks = new UploadCallbackRegistry()
